import * as React from "react";
import {
  type CanvasRef,
  type ZoomResult,
  type LayoutResult,
  Canvas,
  ElkCanvasLayoutOptions,
  CanvasPosition,
  NodeProps,
  EdgeProps,
} from "reaflow";

import workflowCVMStyles from "./WorkflowCVM.module.css";
import { IconZoomIn, IconZoomOut, IconZoomReset } from "@tabler/icons-react";

import { Tooltip, Button, ActionIcon } from "@mantine/core";
import { BaseEdgeDataType, BaseNodeDataType, CanvasAction } from "./WorkflowUtils";
import NodeRouter from "./Node/NodeRouter";
import BaseEdge from "./Edge/BaseEdge";

// Workflow
type WorkflowPropsType = {
  allNodes: BaseNodeDataType[];
  allEdges: BaseEdgeDataType[];
  disabled?: boolean;
  selectedNode?: BaseNodeDataType | null;
  selectedEdge?: BaseEdgeDataType | null;
  onAddNode(edge: BaseEdgeDataType): void;
  onPortDragEnd(fromNodeId: string, toNodeId?: string): void;
  onGroupAdd(node: BaseNodeDataType): void;
  onCanvasClick(): void;
  onNodeClick(node: BaseNodeDataType): void;
  onDeleteNode(node: BaseNodeDataType): void;
  onDeleteEdge(edge: BaseEdgeDataType): void;
  onEdgeClick(edge: BaseEdgeDataType): void;
};

function canvasAction(instance: CanvasRef, type: CanvasAction) {
  switch (type) {
    case "zoomIn":
      return instance.zoomIn ? instance.zoomIn() : undefined;
    case "zoomOut":
      return instance.zoomOut ? instance.zoomOut() : undefined;
    case "zoomReset":
      return instance.fitCanvas ? instance.fitCanvas() : undefined;
  }
}

interface LayoutOptionsProps extends ElkCanvasLayoutOptions {
  "elk.portAlignment.default": "DISTRIBUTED" | "JUSTIFIED" | "BEGIN" | "CENTER" | "END";
}

const LayoutOptions: LayoutOptionsProps = {
  "elk.algorithm": "elk.layered",
  "elk.direction": "RIGHT",
  "elk.portAlignment.default": "CENTER",
  "elk.spacing.nodeNode": "80",
  "elk.spacing.portPort": "1",
  "elk.childAreaWidth": "3000",
  "elk.hierarchyHandling": "INCLUDE_CHILDREN",
};

interface ControlsProps {
  onZoomIn: ZoomResult["zoomIn"];
  onZoomOut: ZoomResult["zoomOut"];
  onZoomReset: LayoutResult["fitCanvas"];
}

interface ControlProps {
  title: string;
  icon: JSX.Element;
  onClick?(): void;
}

const Controls: React.FC<ControlsProps> = ({ onZoomIn, onZoomOut, onZoomReset }) => (
  <div className={workflowCVMStyles.controls}>
    <Control title="Zoom In" onClick={onZoomIn} icon={<IconZoomIn />} />
    <Control title="Zoom Out" onClick={onZoomOut} icon={<IconZoomOut />} />
    <Control title="Reset Zoom" onClick={onZoomReset} icon={<IconZoomReset />} />
  </div>
);

const Control: React.FC<ControlProps> = ({ title, icon, onClick }) => (
  <Tooltip label={title}>
    <ActionIcon size="sm" variant="white" color="gray" onClick={onClick}>
      {icon}
    </ActionIcon>
  </Tooltip>
);

export const Workflow: React.FC<WorkflowPropsType> = ({
  allNodes,
  allEdges,
  disabled,
  selectedNode,
  selectedEdge,
  onCanvasClick,
  onNodeClick,
  onAddNode,
  onGroupAdd,
  onDeleteNode,
  onPortDragEnd,
  onEdgeClick,
  onDeleteEdge,
}) => {
  const canvasRef = React.useRef<CanvasRef>(null);
  const [zoom, setZoom] = React.useState<number>(0.7);

  React.useEffect(() => {
    const svgElement = canvasRef.current?.svgRef.current;
    if (svgElement) {
      svgElement.setAttribute("width", "8000");
    }
  }, []);

  const action = React.useCallback(
    (type: CanvasAction) => {
      if (!canvasRef.current) return undefined;
      return canvasAction(canvasRef.current, type);
    },
    [canvasRef],
  );

  /**
   * Node component. All nodes will render trough this component.
   *
   * Uses the NodeRouter component which will render a different node layout, depending on the node "type".
   */
  const Node = (nodeProps: NodeProps) => {
    return (
      <NodeRouter
        {...nodeProps}
        allNodes={allNodes}
        allEdges={allEdges}
        selectedNode={selectedNode}
        onNodeClick={onNodeClick}
        onDeleteNode={onDeleteNode}
        onPortDragEnd={onPortDragEnd}
        onGroupAdd={onGroupAdd}
        isDisabled={disabled}
      />
    );
  };

  /**
   * Edge component. All edges will render trough this component.
   *
   * All edges render the same way, no matter to which node they're linked to.
   * (There is no "EdgeRouter", unlike with nodes)
   */
  const Edge = (edgeProps: EdgeProps) => {
    return (
      <BaseEdge
        {...edgeProps}
        allNodes={allNodes}
        allEdges={allEdges}
        selectedEdge={selectedEdge}
        onAddNode={onAddNode}
        onDeleteEdge={onDeleteEdge}
        onEdgeClick={onEdgeClick}
        isDisabled={disabled}
      />
    );
  };

  return (
    <div className={workflowCVMStyles.wrapper}>
      <Controls
        onZoomIn={() => action("zoomIn")}
        onZoomOut={() => action("zoomOut")}
        onZoomReset={() => action("zoomReset")}
      />
      <div className={workflowCVMStyles.canvas}>
        <Canvas
          // fit={true}
          pannable
          zoomable
          zoom={zoom}
          maxZoom={0.2}
          minZoom={-0.4}
          ref={canvasRef}
          nodes={allNodes}
          edges={allEdges}
          selections={[]}
          layoutOptions={LayoutOptions}
          defaultPosition={CanvasPosition.LEFT}
          onCanvasClick={onCanvasClick}
          // onLayoutChange={(layout) => console.log("Layout", layout)}
          node={Node}
          edge={Edge}
          onZoomChange={(z) => {
            setZoom(z);
          }}
        />

        {/*         <Canvas
          pannable
          zoomable
          maxZoom={0.2}
          minZoom={-0.4}
          ref={canvasRef}
          nodes={nodes1}
          edges={edges1}
          selections={[]}
          onCanvasClick={onCanvasClick}
          layoutOptions={LayoutOptions}
          defaultPosition={CanvasPosition.LEFT}
          // onLayoutChange={(layout) => console.log("Layout", layout)}
        /> */}
      </div>
    </div>
  );
};
